import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import I18n from '@/modules/i18n';
import CorrelationsContext from '@/components/common/Context';
import Text from '@/components/ui/Text';
import Icon from '@/components/ui/Icon';
import styles from '../Correlations.module.scss';
import { LessonType, TagType, UnitType } from '../types';
import UnitTagContent from './UnitTagContent';
import LessonTagContent from './LessonTagContent';

const MemoizedUnitTagContent = React.memo(UnitTagContent);
const MemoizedLessonTagContent = React.memo(LessonTagContent);

interface TagCardPropType {
  tag: TagType;
  anchored: boolean;
  isSysadmin: boolean;
}

const TagCard = ({ tag, anchored, isSysadmin }: TagCardPropType) => {
  const { programId } = useContext(CorrelationsContext);
  const ref = useRef(null);
  const [expandedItems, setExpandedItems] = useState(new Set<string>());

  const headerLabel = (model: LessonType | UnitType) => {
    const isComingSoon = 'comingSoon' in model && model.comingSoon;
    const title = isComingSoon ? `${model.title} (${I18n.t('coming_soon')})` : model.title;

    if ('titleNumber' in model && model.titleNumber) {
      return `${I18n.t('unit')} ${model.unitPosition} - ${I18n.t('lesson')} ${model.titleNumber} - ${title}`;
    }

    return `${I18n.t('unit')} ${model.unitPosition} - ${title}`;
  };

  useEffect(() => {
    if (!anchored || !ref.current) return;

    ref.current.scrollIntoView();
  }, [ref.current]);

  const toggleExpanded = useCallback((key: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);

      return newSet;
    });
  }, []);

  const toggleAll = () => {
    if (expandedItems.size === tag.units.length + tag.lessons.length) {
      setExpandedItems(() => new Set<string>());
    } else {
      setExpandedItems(() => {
        const newSet = new Set<string>();

        tag.units.forEach(unit => newSet.add(`Unit-${unit.id}`));
        tag.lessons.forEach(unit => newSet.add(`Lesson-${unit.id}`));

        return newSet;
      });
    }
  };

  return (
    <section
      key={tag.id}
      ref={ref}
      aria-label={`Benchmarks for ${tag.alias}`}
      className={`card ${styles.card}`}
      id={tag.alias}
    >
      <div className="tw-flex tw-justify-between">
        <Text size="sm">
          {I18n.locale === 'es' ? tag.description_es : tag.description_en}
        </Text>
        <button
          className="tw-w-7 tw-h-6 btn btn--outline-purple tw-px-1.5 tw-py-1 tw-leading-none tw-box-border tw-shrink-0"
          type="button"
          onClick={toggleAll}
        >
          <Icon
            icon={expandedItems.size === tag.units.length + tag.lessons.length ? 'CollapseRounded' : 'ExpandRounded'}
            size={14}
          />
        </button>
      </div>
      <div className="tw-mt-4">
        {tag.units.map(unit => (
          <MemoizedUnitTagContent
            key={unit.id}
            headerLabel={headerLabel(unit)}
            isExpanded={expandedItems.has(`Unit-${unit.id}`)}
            programId={programId}
            tag={tag}
            toggleExpanded={toggleExpanded}
            unit={unit}
          />
        ))}
      </div>
      <div>
        {tag.lessons.map(lesson => (
          <MemoizedLessonTagContent
            key={lesson.id}
            headerLabel={headerLabel(lesson)}
            isExpanded={expandedItems.has(`Lesson-${lesson.id}`)}
            isSysadmin={isSysadmin}
            lesson={lesson}
            programId={programId}
            tag={tag}
            toggleExpanded={toggleExpanded}
          />
        ))}
      </div>
    </section>
  );
};

export default TagCard;
